import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { useActiveFamily } from '../context/ActiveFamilyContext';

export type MemberRole = 'admin' | 'member' | null;

interface UseMemberRoleResult {
  role: MemberRole;
  isOwner: boolean;
  isAdmin: boolean;
  isMember: boolean;
  isOwnerOrAdmin: boolean;
  loading: boolean;
  memberId: string | null;
}

/**
 * Hook para obtener el rol del miembro actual en la familia activa
 */
export function useMemberRole(): UseMemberRoleResult {
  const { user } = useAuth();
  const { activeFamily } = useActiveFamily();
  const [role, setRole] = useState<MemberRole>(null);
  const [loading, setLoading] = useState(true);
  const [memberId, setMemberId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid || !activeFamily?.id) {
      setRole(null);
      setMemberId(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Buscar el member del usuario en la familia activa
    const membersRef = collection(db, 'members');
    const q = query(
      membersRef,
      where('userId', '==', user.uid),
      where('familyId', '==', activeFamily.id)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (snapshot.empty) {
          // No hay member, verificar si es owner de la familia
          if (activeFamily.ownerId === user.uid) {
            setRole('admin');
            setMemberId(null);
          } else if (activeFamily.admins?.includes(user.uid)) {
            setRole('admin');
            setMemberId(null);
          } else {
            setRole(null);
            setMemberId(null);
          }
        } else {
          const memberDoc = snapshot.docs[0];
          const memberData = memberDoc.data();
          setMemberId(memberDoc.id);
          
          // Determinar rol (solo 'admin' o 'member')
          // Owner es una propiedad separada (activeFamily.ownerId)
          if (activeFamily.ownerId === user.uid) {
            setRole('admin');
          } else if (activeFamily.admins?.includes(user.uid)) {
            setRole('admin');
          } else if (memberData.role === 'admin') {
            setRole('admin');
          } else {
            setRole('member');
          }
        }
        setLoading(false);
      },
      (error) => {
        setRole(null);
        setMemberId(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, activeFamily?.id, activeFamily?.ownerId, activeFamily?.admins]);

  const result = {
    role,
    isOwner: activeFamily?.ownerId === user?.uid,
    isAdmin: role === 'admin',
    isMember: role === 'member',
    isOwnerOrAdmin: activeFamily?.ownerId === user?.uid || role === 'admin',
    loading,
    memberId,
  };
  
  return result;
}
